import { user_anonymous } from '~/lib/auth';
import { ActionFunctionArgs, LoaderFunctionArgs, redirect, json } from '@remix-run/cloudflare';
import { session_load, session_save } from '~/lib/session';
import { NavLink, useActionData, useLoaderData } from '@remix-run/react';
import { db_user_by_email, db_user_create, db_user_entity } from '~/.server/drizzle/repository/user';
import z from 'zod';
import { zodTEmailRequired } from '~/lib/zod';
import ZodForm from '~/components/zod/ZodForm';
import ZodSubmitButton from '~/components/zod/ZodSubmitButton';
import ZodFieldInput from '~/components/zod/ZodFieldInput';
import FormGroup from '~/components/form/FormGroup';
import Heading from '~/components/layout/Heading';
import Link from '~/components/interactive/Link';
import IconGoogle from '~/components/icons/IconGoogle';
import { google_oauth_authorize_callback, google_oauth_authorize_url } from '~/.server/vendor/google';
import { cf_build_public_url } from '~/lib/cloudflare';
import NavigationButton from '~/components/interactive/NavigationButton';
import { v4 } from 'uuid';
import Message from '~/components/Message';
import { remix_schema_post } from '~common/.server/remix';
import { tenant_require } from '~/tenants/common/util.server';
import { remix_brand_id } from '~lib/.server/request';
import { staff_email } from '~/lib/staff';

const schema = z.object({
  email: zodTEmailRequired(),
  password: z.string(),
});

export async function loader(args: LoaderFunctionArgs) {
  await user_anonymous(args);

  const url = new URL(args.request.url);

  const redirected = cf_build_public_url(args, '/login');
  const token = url.searchParams.get('token');
  const userId = url.searchParams.get('user');

  let error: string | undefined;

  if (token && userId) {
    const user = await db_user_entity(args, userId);

    if ((await user.verifyUrlToken(url)) && !user.disabled) {
      throw redirect(url.pathname, {
        headers: await session_save(args, { user_id: userId }),
      });
    }

    error = 'The link you clicked has expired, please login with your credentials.';
  }

  const session = await session_load(args);

  if (url.searchParams.has('code')) {
    if (session?.verifier) {
      const callback = await google_oauth_authorize_callback(args, args.request.url, session.verifier, redirected);

      const email = zodTEmailRequired().safeParse(callback.user.email);

      if (email.success) {
        const user = await db_user_by_email(args, email.data);

        if (user) {
          if (!user.verified) {
            await user.db_verify();
          }

          if (!user.disabled) {
            throw redirect(session.redirect ?? '/', {
              headers: await session_save(args, { user_id: user.id }),
            });
          }
        } else {
          const id = v4();

          await db_user_create({
            id,
            ctx: args,
            email: email.data,
            admin: staff_email(email.data),
            data: {
              google: true,
              password_change: true,
              brand: remix_brand_id(args),
              first: callback.user.given_name,
              last: callback.user.family_name,
              accepted: new Date().toISOString(),
              verified: true,
            },
          });

          throw redirect(session.redirect ?? '/', {
            headers: await session_save(args, { user_id: id }),
          });
        }
      }
    }
  }

  const env = args.context.env;
  if (!env?.GOOGLE_SECRET) {
    // If GOOGLE_SECRET is missing, we won't provide an OAuth URL
    return json(
      { google: null, error },
      { headers: await session_save(args, { user_id: null, verifier: undefined, redirect: session?.redirect }) },
    );
  }

  const google = await google_oauth_authorize_url(args, redirected);

  return json(
    { google: google.url, error },
    {
      headers: await session_save(args, { user_id: null, verifier: google.codeVerifier, redirect: session?.redirect }),
    },
  );
}

export async function action(args: ActionFunctionArgs) {
  const { email, password } = await remix_schema_post(args, schema);

  const tenant = tenant_require(args);

  const user = await db_user_by_email(args, email);

  if (!user) {
    // not best practice but causes support issues
    return { error: tenant.emailMissingMessage ?? 'That email does not exist in our system.' };
  }

  if (!user.verified) {
    return { error: 'Please verify your email address before logging in. Check your email for the verification link.' };
  }

  if (user.disabled) {
    return { error: 'Your account has been disabled.' };
  }

  if (user?.comparePassword(password)) {
    const session = await session_load(args);

    throw redirect(session?.redirect ?? '/', {
      headers: await session_save(args, { user_id: user.id }),
    });
  }

  return { error: 'The password you provided is invalid.' };
}

export default function () {
  const response = useActionData<typeof action>();
  const { google, error } = useLoaderData<typeof loader>();

  return (
    <>
      {(response?.error ?? error) && (
        <div className="mb-6">
          <Message type="error">{response?.error ?? error}</Message>
        </div>
      )}
      <div className="pb-6 max-sm:hidden text-left">
        <Heading title="Welcome" size="xl" />
      </div>
      <ZodForm schema={schema}>
        <FormGroup>
          <ZodFieldInput name="email" placeholder="Email Address" autoFocus />
          <ZodFieldInput name="password" type="password" placeholder="Password" />
          <div className="flex items-center justify-between">
            <NavLink to="/forgot" className="text-xs font-medium">
              Forgot password?
            </NavLink>
            <ZodSubmitButton variant="tenant" id="login">
              Log In
            </ZodSubmitButton>
          </div>
        </FormGroup>
      </ZodForm>
      {google && (
        <div className="flex flex-col items-center border-t border-theme-separator mt-6 pt-4 text-xs">
          <div className="font-semibold mb-2">
            Continue with Google if you already have a Gmail account you'd like to use:
          </div>
          <NavigationButton to={google} icon={<IconGoogle />} variant="outline" popup={false}>
            Google / Gmail
          </NavigationButton>
        </div>
      )}
      <div className="flex flex-col items-center border-t border-theme-separator mt-4 pt-4 font-semibold">
        <div>
          Don't have an account? <Link to="/register">Sign up here</Link>
        </div>
      </div>
    </>
  );
}
